import React, { useEffect, useRef } from 'react';
import { useTour } from '../contexts/TourContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useTheme } from '../contexts/ThemeContext';
import { applyCompanionHighlight, clearCompanionHighlights } from '../utils/companionHighlight';

const VirtualTour: React.FC = () => {
  const { language } = useLanguage();
  const { isDarkMode } = useTheme();
  const tourRef = useRef<HTMLDivElement>(null);
  const ar = language === 'ar';

  const {
    isTourActive,
    currentStep,
    currentSection,
    sections,
    startTour,
    stopTour,
    nextStep,
    previousStep,
    goToSection,
    highlightElement,
    clearHighlight,
    getCurrentStep,
    getCurrentSection,
    markStepCompleted,
    resetTour,
    changeTab,
    executeAction,
  } = useTour();

  const currentStepData = getCurrentStep();
  const currentSectionData = getCurrentSection();

  useEffect(() => {
    if (isTourActive && currentStepData) {
      // Execute tab change if specified
      if (currentStepData.tabToChange) {
        changeTab(currentStepData.tabToChange);
      }

      // Execute action if specified
      if (currentStepData.action && currentStepData.action !== 'none') {
        executeAction(currentStepData.action, currentStepData.target);
      }

      // Highlight the target element
      if (currentStepData.highlight && currentStepData.target !== 'body') {
        highlightElement(currentStepData.target);
      } else {
        clearHighlight();
      }

      // Scroll to the target element
      if (currentStepData.target !== 'body') {
        const targetElement = document.querySelector(currentStepData.target);
        if (targetElement) {
          targetElement.scrollIntoView({
            behavior: 'smooth',
            block: 'center'
          });
        } else {
          }
      }

      if (currentStepData.highlight && currentStepData.target !== 'body') {
        const desc = ar ? currentStepData.descriptionAr : currentStepData.description;
        applyCompanionHighlight({
          selector: currentStepData.target,
          message: desc.slice(0, 220),
        });
      } else {
        clearCompanionHighlights();
      }

      markStepCompleted(currentStepData.id);
      }
  }, [isTourActive, currentStepData, ar, highlightElement, clearHighlight, markStepCompleted, changeTab, executeAction]);

  useEffect(() => {
    if (!isTourActive) clearCompanionHighlights();
  }, [isTourActive]);

  if (!isTourActive || !currentStepData || !currentSectionData) {
    return null;
  }

  const isFirstStep = currentStep === 0 && currentSection === 0;
  const isLastStep = currentStep === currentSectionData.steps.length - 1 && currentSection === sections.length - 1;

  const getPositionStyles = () => {
    if (currentStepData.target === 'body') {
      return {
        position: 'fixed' as const,
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        zIndex: 10060
      };
    }

    const targetElement = document.querySelector(currentStepData.target);
    if (!targetElement) {
      return {
        position: 'fixed' as const,
        top: '20px',
        right: '20px',
        zIndex: 10060
      };
    }

    const rect = targetElement.getBoundingClientRect();
    const tourHeight = tourRef.current?.offsetHeight || 400;
    const tourWidth = tourRef.current?.offsetWidth || 400;

    let top = rect.bottom + 10;
    let left = rect.left;

    // Adjust position based on tour step position preference
    switch (currentStepData.position) {
      case 'top':
        top = rect.top - tourHeight - 10;
        break;
      case 'bottom':
        top = rect.bottom + 10;
        break;
      case 'left':
        top = rect.top;
        left = rect.left - tourWidth - 10;
        break;
      case 'right':
        top = rect.top;
        left = rect.right + 10;
        break;
      case 'center':
        top = rect.top + (rect.height / 2) - (tourHeight / 2);
        left = rect.left + (rect.width / 2) - (tourWidth / 2);
        break;
    }

    // Ensure tour stays within viewport with better positioning
    const minTop = 80; // Keep away from top header
    const minBottom = 20;
    const minLeft = 20;
    const minRight = 20;
    
    if (top < minTop) top = minTop;
    if (top + tourHeight > window.innerHeight - minBottom) {
      top = window.innerHeight - tourHeight - minBottom;
    }
    if (left < minLeft) left = minLeft;
    if (left + tourWidth > window.innerWidth - minRight) {
      left = window.innerWidth - tourWidth - minRight;
    }
    
    // If still too close to top, move to a safer position
    if (top < 100) {
      top = 100;
    }

    return {
      position: 'fixed' as const,
      top: `${top}px`,
      left: `${left}px`,
      zIndex: 10000
    };
  };

  return (
    <>
      {/* Overlay - No blur, just semi-transparent */}
      <div
        className="fixed inset-0 bg-black bg-opacity-30 z-[10055]"
        onClick={stopTour}
      />

      {/* Tour Tooltip */}
      <div
        ref={tourRef}
        dir={ar ? 'rtl' : 'ltr'}
        className="fixed z-[10060] max-w-lg"
        style={{
          ...getPositionStyles(),
          background: 'var(--elev)',
          color: 'var(--txt)',
          border: '1px solid var(--border)',
          borderRadius: 12,
          boxShadow: 'var(--lift-3)',
          fontFamily: 'var(--font-ui)',
          animation: 'tourPulse 2s ease-in-out infinite'
        }}
      >
        {/* Header */}
        <div className="p-4 border-b" style={{ borderColor: 'var(--hair)' }}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-full" style={{ background: 'var(--acc)', color: 'var(--void)', animation: 'tourIconSpin 3s linear infinite' }}>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <h3 className="text-sm font-semibold">
                  {ar ? currentStepData.titleAr : currentStepData.title}
                </h3>
                <p className="text-xs" style={{ color: 'var(--muted)' }}>
                  {ar ? 'مرافق MMEC · ' : 'MMEC Companion · '}
                  {ar ? currentSectionData.titleAr : currentSectionData.title}
                </p>
              </div>
            </div>
            <button
              onClick={stopTour}
              className="mmec-btn is-ghost"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-4">
          {/* Description */}
          <div className="mb-4">
            <p className="text-sm leading-relaxed">
              {language === 'ar' ? currentStepData.descriptionAr : currentStepData.description}
            </p>
          </div>

          {/* Importance */}
          {currentStepData.importance && (
            <div className="mb-4 p-3 rounded-lg" style={{ background: 'var(--acc-dim)', border: '1px solid color-mix(in srgb, var(--acc) 35%, transparent)' }}>
              <h4 className="font-semibold text-sm mb-2" style={{ color: 'var(--acc)' }}>
                {language === 'ar' ? 'الأهمية:' : 'Importance:'}
              </h4>
              <p className="text-sm">
                {language === 'ar' ? currentStepData.importanceAr : currentStepData.importance}
              </p>
            </div>
          )}

          {/* Examples */}
          {currentStepData.examples && currentStepData.examples.length > 0 && (
            <div className="mb-4">
              <h4 className="font-semibold text-sm mb-2" style={{ color: 'var(--up)' }}>
                {language === 'ar' ? 'أمثلة:' : 'Examples:'}
              </h4>
              <ul className="text-sm space-y-1">
                {(language === 'ar' ? currentStepData.examplesAr : currentStepData.examples)?.map((example, index) => (
                  <li key={index} className="flex items-start">
                    <span className="mr-2 mt-1 w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: 'var(--up)' }} />
                    {example}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Tips */}
          {currentStepData.tips && currentStepData.tips.length > 0 && (
            <div className="mb-4">
              <h4 className="font-semibold text-sm mb-2" style={{ color: 'var(--warn)' }}>
                {language === 'ar' ? 'نصائح:' : 'Tips:'}
              </h4>
              <ul className="text-sm space-y-1">
                {(language === 'ar' ? currentStepData.tipsAr : currentStepData.tips)?.map((tip, index) => (
                  <li key={index} className="flex items-start">
                    <span className="mr-2 mt-1 w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: 'var(--warn)' }} />
                    {tip}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Action Instructions */}
          {currentStepData.action && currentStepData.action !== 'none' && (
            <div className={`mb-4 p-3 rounded-lg ${
              isDarkMode ? 'bg-purple-900 bg-opacity-30 border border-purple-700' : 'bg-purple-50 border border-purple-200'
            }`}>
              <h4 className={`font-semibold text-sm mb-2 ${
                isDarkMode ? 'text-purple-300' : 'text-purple-700'
              }`}>
                {language === 'ar' ? 'التفاعل المطلوب:' : 'Required Action:'}
              </h4>
              <p className="text-sm">
                {currentStepData.action === 'click' && (language === 'ar' ? 'انقر على العنصر المظلل' : 'Click on the highlighted element')}
                {currentStepData.action === 'hover' && (language === 'ar' ? 'حرك المؤشر فوق العنصر المظلل' : 'Hover over the highlighted element')}
                {currentStepData.action === 'scroll' && (language === 'ar' ? 'مرر للأسفل لرؤية المزيد' : 'Scroll down to see more')}
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t" style={{ borderColor: 'var(--hair)' }}>
          {/* Progress */}
          <div className="flex items-center justify-between mb-3" style={{ fontSize: 12, color: 'var(--muted)', fontVariantNumeric: 'tabular-nums' }}>
            <span>
              {language === 'ar' ? 'التقدم:' : 'Progress:'} {currentStep + 1} / {currentSectionData.steps.length}
            </span>
            <span>
              {language === 'ar' ? 'القسم:' : 'Section:'} {currentSection + 1} / {sections.length}
            </span>
          </div>

          {/* Progress Bar */}
          <div className="w-full h-2 rounded-full mb-3" style={{ background: 'var(--elev2)' }}>
            <div
              className="h-full rounded-full transition-all duration-300"
              style={{
                background: 'var(--acc)',
                width: `${((currentSection * 100) + ((currentStep / currentSectionData.steps.length) * 100)) / sections.length}%`
              }}
            />
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-between gap-2">
            <div className="flex gap-2">
              <button
                onClick={startTour}
                className="mmec-btn"
              >
                {language === 'ar' ? 'إعادة البداية' : 'Restart'}
              </button>
              <button
                onClick={resetTour}
                className="mmec-btn"
              >
                {language === 'ar' ? 'إعادة تعيين' : 'Reset'}
              </button>
              {/* Go to this tour button */}
              <button
                onClick={() => goToSection(currentSection)}
                className="mmec-btn"
              >
                {language === 'ar' ? 'اذهب لهذا القسم' : 'Go to this tour'}
              </button>
            </div>

            <div className="flex gap-2">
              <button
                onClick={previousStep}
                disabled={isFirstStep}
                className={`mmec-btn${isFirstStep ? ' opacity-50 cursor-not-allowed' : ''}`}
              >
                {language === 'ar' ? 'السابق' : 'Previous'}
              </button>
              <button
                onClick={nextStep}
                className="mmec-btn is-active"
              >
                {isLastStep 
                  ? (language === 'ar' ? 'إنهاء' : 'Finish')
                  : (language === 'ar' ? 'التالي' : 'Next')
                }
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default VirtualTour; 